<script lang="ts" setup>
import { computed, PropType } from 'vue';
import {
  TirPmButton,
  TirPmButtonSizeEnum,
  TirPmButtonVariantEnum,
  TirPmIconView,
  TirPmSvg,
} from 'tir-components';
import { QueueRelationModel } from '../../models';
import { ConnectIcon, FolderIcon } from 'tir-style-system/icons/outline';
import { ListBulletIcon } from 'tir-style-system/icons/solid';
import { TirPmTooltipElement } from 'tir-pm-tooltip-element';
import { RouteLocationRaw, RouterLink, useRouter } from 'vue-router';

/**Роутер */
const router = useRouter();

const props = defineProps({
  /**Очередь */
  queue: {
    type: Object as PropType<QueueRelationModel>,
    required: true,
  },
  /**Текущая очередь */
  isCurrentQueue: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits<{
  /**Событие выбора очереди */
  select: [];
}>();

/**Автор родительской задачи */
const parentTaskAuthor = computed(() => props.queue.ParentTask?.Author);
/**Текст для аватарки автора родительской задачи очереди */
const parentTaskAuthorAvatarText = computed(() => {
  const first = parentTaskAuthor.value?.FirstName?.[0]?.toLocaleUpperCase() ?? '';
  const last = parentTaskAuthor.value?.LastName?.[0]?.toLocaleUpperCase() ?? '';
  return first + last;
});
/**Текст для тултипа автора родительской задачи очереди */
const parentTaskAuthorTooltipText = computed(() => {
  if (!parentTaskAuthor.value) return;

  return `${parentTaskAuthor.value.LastName} ${parentTaskAuthor.value.FirstName} ${parentTaskAuthor.value.MiddleName}`;
});

const parentTaskRoute = computed<RouteLocationRaw | undefined>(() => {
  if (props.queue.ParentTask?.Access)
    return {
      name: 'task-detail',
      params: {
        taskId: props.queue.ParentTask.TaskId,
        queueId: props.queue.ParentTask.QueueId,
        namespaceId: props.queue.ParentTask.NamespaceId,
        projectId: props.queue.ParentTask.ProjectId,
      },
    };
});

/**Обработчик события `click` */
const onClick = () => {
  if (props.queue.ParentTask || props.isCurrentQueue) return;
  emit('select');
};
</script>

<template>
  <div
    :class="[
      'task-queue-search-dropdown-option',
      { 'with-parent': !!queue.ParentTask || isCurrentQueue },
    ]"
    @click="onClick"
  >
    <div class="queue-option__info">
      <div class="queue-option__header">
        <ListBulletIcon class="queue-option__queue-icon" />
        <span class="queue-option__queue-name" :title="queue.Name">
          {{ queue.Name }}
        </span>
      </div>
      <div class="queue-option__path">
        <div
          class="queue-option__path-section"
          :style="{ flexShrink: queue.Namespace.Name.length }"
        >
          <TirPmSvg
            :url="queue.Namespace.Icon"
            class="queue-option__path-section-namespace-icon"
            :color="queue.Namespace.Color"
          />
          <span class="queue-option__path-section-text" :title="queue.Namespace.Name">
            {{ queue.Namespace.Name }}
          </span>
          <div class="queue-option__path-separator">></div>
        </div>
        <div
          class="queue-option__path-section"
          v-for="group in queue.Groups"
          :style="{ flexShrink: group.Name.length }"
          :key="group.Id"
        >
          <FolderIcon class="queue-option__path-section-icon" />
          <span class="queue-option__path-section-text" :title="group.Name">
            {{ group.Name }}
          </span>
          <div class="queue-option__path-separator">></div>
        </div>
        <div class="queue-option__path-section" :style="{ flexShrink: queue.Name.length }">
          <ListBulletIcon class="queue-option__path-section-icon" />
          <span class="queue-option__path-section-text" :title="queue.Name">
            {{ queue.Name }}
          </span>
        </div>
      </div>
    </div>
    <component
      :is="parentTaskRoute ? RouterLink : 'span'"
      v-if="queue.ParentTask"
      :to="parentTaskRoute"
    >
      <TirPmButton
        class="queue-option__parent-task-button"
        :variant="TirPmButtonVariantEnum.SecondaryState"
        :size="TirPmButtonSizeEnum.Small"
        :is-disabled="!queue.ParentTask.Access"
        @click.stop
      >
        <div class="queue-option__parent-task-button-content">
          <TirPmTooltipElement class="queue-option__tooltip-element">
            <TirPmIconView
              v-if="parentTaskAuthor && !isCurrentQueue"
              class="queue-option__parent-task-author"
              :size="24"
              :is-rounded="true"
              :is-bordered="false"
              :image-load-url="parentTaskAuthor.ImageUrl"
              :color-code="parentTaskAuthor.AvatarColor"
            >
              <template #mainSlot>
                <span
                  class="queue-option__parent-task-avatar-text"
                  v-if="parentTaskAuthorAvatarText"
                >
                  {{ parentTaskAuthorAvatarText }}
                </span>
              </template>
            </TirPmIconView>
            <template #content>
              <span v-if="parentTaskAuthorTooltipText">
                {{ parentTaskAuthorTooltipText }}
              </span>
            </template>
          </TirPmTooltipElement>
          <span class="queue-option__parent-task-id">
            {{ queue.ParentTask.TaskId }}
          </span>
          <ConnectIcon class="queue-option__parent-task-icon" />
        </div>
      </TirPmButton>
    </component>
  </div>
</template>

<style lang="scss" scoped>
.task-queue-search-dropdown-option {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  width: 100%;
  padding: 0.75rem;

  .queue-option {
    &__info {
      display: flex;
      flex-direction: column;
      gap: 0.25rem;
      flex: 1;
      min-width: 0;
    }
    &__header {
      display: flex;
      align-items: center;
      gap: 0.25rem;
      color: var(--text-primary, #272d37);
    }
    &__queue-icon {
      width: 1rem;
      height: 1rem;
      flex-shrink: 0;
    }
    &__queue-name {
      overflow: hidden;
      text-overflow: ellipsis;
      font-size: 0.875rem;
      font-weight: 500;
      line-height: 1.25rem;
      white-space: nowrap;
    }
    &__path {
      display: flex;
      align-items: center;
      gap: 0.25rem;
    }
    &__path-section {
      display: flex;
      align-items: center;
      gap: 0.25rem;
      min-width: 2rem;
    }
    &__path-section-text {
      display: block;
      color: var(--text-tertiary, #93969b);
      font-size: 0.688rem;
      font-weight: 500;
      line-height: 1rem;
      min-width: 0;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    &__path-section-namespace-icon {
      font-size: 1rem;
      flex-shrink: 0;
    }
    &__parent-task-button-content {
      display: flex;
      align-items: center;
      gap: 0.375rem;
    }
    &__tooltip-element {
      width: fit-content;
      flex-shrink: 0;
    }
    &__parent-task-id {
      font-size: 0.875rem;
      font-weight: 500;
      line-height: 1.25rem;
      flex-shrink: 0;
    }
    &__parent-task-icon {
      width: 1rem;
      height: 1rem;
      flex-shrink: 0;
    }
    &__path-section-icon {
      width: 1rem;
      height: 1rem;
      flex-shrink: 0;
      color: var(--text-tertiary, #93969b);
    }
    &__path-separator {
      color: var(--text-tertiary, #93969b);
      font-size: 0.688rem;
      font-weight: 500;
      line-height: 1rem;
      flex-shrink: 0;
    }
  }

  &.with-parent {
    cursor: default;
    background-color: var(--bg-base, #fff);
    .queue-option {
      &__header {
        color: var(--text-tertiary, #93969b);
      }
    }
  }
}
</style>

<style lang="scss">
.queue-option__parent-task-button.button {
  border-radius: 0.375rem;
  .button__sign {
    padding: 0;
  }
}
</style>
